/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { ZoneSummaryInstance } from '@modules/ui-types'

import { getRawCpu } from '@/components/Topology/DeploymentTemplates/components/DeploymentInfrastructure/TopologyElement/helpers'

import type { ZoneSummaryInstanceDiff } from './types'

export function getDiffs(
  oldInstance: ZoneSummaryInstance,
  newInstance: ZoneSummaryInstance | undefined,
): ZoneSummaryInstanceDiff | null {
  if (newInstance === undefined) {
    return null
  }

  const oldTotalSize = oldInstance.totalSize ?? 0
  const newTotalSize = newInstance.totalSize ?? 0
  const totalSizeDiff = newTotalSize - oldTotalSize

  // Calculate actual storage diff (in MB)
  // Storage = totalSize × storageMultiplier
  const oldStorage = oldTotalSize * (oldInstance.storageMultiplier ?? 0)
  const newStorage = newTotalSize * (newInstance.storageMultiplier ?? 0)
  const storageDiff = newStorage - oldStorage

  // Calculate actual vCPU diff
  const cpuDiff = getCpuDiff(oldInstance, newInstance)

  return {
    totalSizeDiff,
    storageDiff,
    cpuDiff,
  }
}

/**
 * Calculate the CPU difference between two instances.
 * Uses getRawCpu to account for CPU boosting on small instances (≤ 8GB).
 */
function getCpuDiff(oldInstance: ZoneSummaryInstance, newInstance: ZoneSummaryInstance): number {
  const oldTotalSize = oldInstance.totalSize ?? 0
  const newTotalSize = newInstance.totalSize ?? 0
  const oldCpuMultiplier = oldInstance.cpuMultiplier ?? 0
  const newCpuMultiplier = newInstance.cpuMultiplier ?? 0

  // Use getRawCpu to account for boosting on small instances (≤ 8GB)
  const oldCpu = getRawCpu({
    cpuMultiplier: oldCpuMultiplier,
    totalSize: oldTotalSize,
    skipSizeCheck: false,
  })
  const newCpu = getRawCpu({
    cpuMultiplier: newCpuMultiplier,
    totalSize: newTotalSize,
    skipSizeCheck: false,
  })

  const diff = Number(newCpu) - Number(oldCpu)

  // Ignore tiny diffs (less than 0.05 vCPU) to handle floating point precision
  if (Math.abs(diff) < 0.05) {
    return 0
  }

  return diff
}
